import { useState } from "react";
import dayjs from "dayjs";
import { ReactComponent as DomainIcon } from "../../../../assets/icons/marketingDashbaord/Domain.svg";
import { ReactComponent as DesignIcon } from "../../../../assets/icons/marketingDashbaord/Design.svg";
import { ReactComponent as HostingIcon } from "../../../../assets/icons/marketingDashbaord/Hosting.svg";
import { ReactComponent as SortIcon } from "../../../../assets/icons/paraplanningDashboard/Sort.svg";
import { ReactComponent as ArrowRightIcon } from "../../../../assets/icons/arrow-right.svg";

const formatDate = (value) => {
  if (!value) return "-";
  const date = dayjs(value?.toDate?.() || value);
  return date.isValid() ? date.format("DD/MM/YYYY") : "-";
};

const formatCurrency = (value) => {
  if (value === "" || value === null || value === undefined) return "-";
  const amount = Number(value);
  return Number.isFinite(amount)
    ? new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(amount)
    : "-";
};

const formatChargeAcross = (value) => {
  if (!value) return "-";
  return `${value} ${Number(value) === 1 ? "installment" : "installments"}`;
};

const getDomainUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const normaliseGroupValue = (value) => String(value || "").trim().toLowerCase();

const toChargeAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const groupWebsiteCharges = (websites) => {
  const groups = new Map();

  websites.forEach((website) => {
    const groupKey =
      normaliseGroupValue(website.adviserEmail) ||
      normaliseGroupValue(website.adviserName) ||
      website.id;
    const domains = website.domains?.length ? website.domains : [{}];

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        key: groupKey,
        adviserName: website.adviserName || website.adviserEmail || "-",
        rows: [],
      });
    }

    domains.forEach((domain, domainIndex) => {
      groups.get(groupKey).rows.push({ website, domain, domainIndex });
    });
  });

  return [...groups.values()];
};

const getDateValue = (value) => {
  if (!value) return 0;
  const date = dayjs(value?.toDate?.() || value);
  return date.isValid() ? date.valueOf() : 0;
};

const WebsiteChargesTable = ({
  websites = [],
  loading = false,
  onOpen,
  showTotals = false,
  searchTerm = "",
}) => {
  const [sort, setSort] = useState({ key: "adviser", direction: "asc" });
  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      direction:
        current.key === key
          ? current.direction === "asc" ? "desc" : "asc"
          : key === "adviser" ? "asc" : "desc",
    }));
  };
  const direction = sort.direction === "asc" ? 1 : -1;
  const normalisedSearch = searchTerm.trim().toLowerCase();
  const websitesWithCharges = websites
    .map((website) => {
      const { domains = [], ...websiteFields } = website;
      const websiteMatches = !normalisedSearch || JSON.stringify(websiteFields).toLowerCase().includes(normalisedSearch);
      return {
        ...website,
        domains: domains.filter(
          (domain) =>
            (String(domain.datePaid || "").trim() || String(domain.design?.liveDate || "").trim()) &&
            (websiteMatches || [JSON.stringify(domain), formatDate(domain.datePaid), formatDate(domain.design?.liveDate)].join(" ").toLowerCase().includes(normalisedSearch)),
        ),
      };
    })
    .filter((website) => website.domains.length);
  const adviserGroups = groupWebsiteCharges(websitesWithCharges)
    .map((group) => ({
      ...group,
      rows: [...group.rows].sort((left, right) => {
        if (sort.key === "datePaid") {
          return direction * (getDateValue(left.domain.datePaid) - getDateValue(right.domain.datePaid));
        }
        if (sort.key === "liveDate") {
          return direction * (getDateValue(left.domain.design?.liveDate) - getDateValue(right.domain.design?.liveDate));
        }
        return 0;
      }),
    }))
    .sort((left, right) => {
      if (sort.key === "adviser") {
        return direction * left.adviserName.localeCompare(right.adviserName);
      }

      const getGroupDate = (group) => Math.max(
        ...group.rows.map(({ domain }) =>
          getDateValue(sort.key === "datePaid" ? domain.datePaid : domain.design?.liveDate),
        ),
      );
      return direction * (getGroupDate(left) - getGroupDate(right));
    });
  const totals = adviserGroups.reduce(
    (currentTotals, group) =>
      group.rows.reduce(
        (groupTotals, { domain }) => ({
          domain:
            groupTotals.domain + toChargeAmount(domain.amountToCharge),
          design:
            groupTotals.design +
            toChargeAmount(domain.design?.amountToCharge),
          hosting:
            groupTotals.hosting +
            toChargeAmount(domain.hosting?.monthlyCharge),
        }),
        currentTotals,
      ),
    { domain: 0, design: 0, hosting: 0 },
  );

  return (
    <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
      <table className="website-table website-charges-table">
        <thead>
          <tr>
            <th
              className="table-group-header table-index-column"
              rowSpan=""
            ></th>
            <th className="table-group-header">Adviser</th>
            <th
              className="table-group-header website-charge-group-divider"
              colSpan="3"
            >
              <span className="table-group-title">
                <DomainIcon /> Domain
              </span>
            </th>
            <th
              className="table-group-header website-charge-group-divider"
              colSpan="3"
            >
              <span className="table-group-title">
                <DesignIcon /> Design
              </span>
            </th>
            <th
              className="table-group-header website-charge-group-divider"
              colSpan="1"
            >
              <span className="table-group-title">
                <HostingIcon /> Hosting
              </span>
            </th>
          </tr>
          <tr>
            <th className="FP_Help_Text_Semibold"></th>
            <th className="FP_Help_Text_Semibold">
              <button
                type="button"
                className="table-heading-content table-sort-button"
                onClick={() => toggleSort("adviser")}
              >
                Adviser <SortIcon aria-hidden />
              </button>
            </th>
            <th className="FP_Help_Text_Semibold website-charge-group-divider">
              URL
            </th>
            <th className="FP_Help_Text_Semibold">
              <button
                type="button"
                className="table-heading-content table-sort-button"
                onClick={() => toggleSort("datePaid")}
              >
                Date Paid <SortIcon aria-hidden />
              </button>
            </th>
            <th className="FP_Help_Text_Semibold">Amount to Charge</th>
            <th className="FP_Help_Text_Semibold website-charge-group-divider">
              <button
                type="button"
                className="table-heading-content table-sort-button"
                onClick={() => toggleSort("liveDate")}
              >
                Live Date <SortIcon aria-hidden />
              </button>
            </th>
            <th className="FP_Help_Text_Semibold">Amount to Charge</th>
            <th className="FP_Help_Text_Semibold">Charge Across</th>
            <th className="FP_Help_Text_Semibold website-charge-group-divider">
              Monthly Charge
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td className="FP_Content primary" colSpan="9">
                Loading website charges...
              </td>
            </tr>
          ) : adviserGroups.length ? (
            adviserGroups.flatMap((group, groupIndex) =>
              group.rows.map(({ website, domain, domainIndex }, rowIndex) => (
                <tr
                  key={`${website.id}-${domainIndex}`}
                  className={`${groupIndex % 2 ? "website-charge-group--even" : "website-charge-group--odd"}${rowIndex === 0 ? " website-charge-group-start" : ""}`}
                  tabIndex="0"
                  onClick={() => onOpen(website.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") onOpen(website.id);
                  }}
                >
                  <td className="FP_Help_Text black table-index-column">
                    {adviserGroups
                      .slice(0, groupIndex)
                      .reduce(
                        (total, adviserGroup) =>
                          total + adviserGroup.rows.length,
                        0,
                      ) +
                      rowIndex +
                      1}
                    .
                  </td>
                  {rowIndex === 0 && (
                    <td
                      className="FP_Help_Text black website-charge-adviser"
                      rowSpan={group.rows.length}
                    >
                      <strong>{group.adviserName}</strong>
                    </td>
                  )}
                  <td className="FP_Help_Text black website-charge-group-divider">
                    {domain.domainName ? (
                      <a
                        className="table-url-link"
                        href={getDomainUrl(domain.domainName)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {domain.domainName}
                      </a>
                    ) : "-"}
                  </td>
                  <td className="FP_Help_Text black">
                    {formatDate(domain.datePaid)}
                  </td>
                  <td className="FP_Help_Text black">
                    {formatCurrency(domain.amountToCharge)}
                  </td>
                  <td className="FP_Help_Text black website-charge-group-divider">
                    {formatDate(domain.design?.liveDate)}
                  </td>
                  <td className="FP_Help_Text black">
                    {formatCurrency(domain.design?.amountToCharge)}
                  </td>
                  <td className="FP_Help_Text black">
                    {formatChargeAcross(domain.design?.chargeAcross)}
                  </td>
                  <td className="FP_Help_Text black website-charge-group-divider">
                    <div className="table-value-action">
                      <span>
                        {formatCurrency(domain.hosting?.monthlyCharge)}
                      </span>
                      <button
                        type="button"
                        className="table-row-action"
                        aria-label={`View ${website.adviserName || "website"} details`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpen(website.id);
                        }}
                      >
                        <ArrowRightIcon aria-hidden />
                      </button>
                    </div>
                  </td>
                </tr>
              )),
            )
          ) : (
            <tr>
              <td className="FP_Content primary" colSpan="9">
                No website charges found.
              </td>
            </tr>
          )}
        </tbody>
        {showTotals && !loading && adviserGroups.length > 0 && (
          <tfoot>
            <tr className="website-charges-total-row">
              <td className="FP_Help_Text black" colSpan="2">
                Total
              </td>
              <td className="website-charge-group-divider" aria-hidden="true" />
              <td aria-hidden="true" />
              <td className="FP_Help_Text black">
                {formatCurrency(totals.domain)}
              </td>
              <td className="website-charge-group-divider" aria-hidden="true" />
              <td className="FP_Help_Text black">
                {formatCurrency(totals.design)}
              </td>
              <td aria-hidden="true" />
              <td className="FP_Help_Text black website-charge-group-divider">
                {formatCurrency(totals.hosting)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default WebsiteChargesTable;
